import type { FieldValues, UseFormStateReturn } from 'react-hook-form';
import type { Nullable } from 'corgi-types';
import type {
  ValidationRuleAge as GqlValidationRuleAge,
  ValidationRulePattern as GqlValidatioRulePattern,
} from 'graphql-ts-types';

import type { Translate } from '../../providers/LocaleProvider';
import type { BirthDayFieldValue } from '../Form/BirthdayField/types';

export const enum FieldType {
  Checkbox = 'checkbox',
  Email = 'email',
  Password = 'password',
  Radio = 'radio',
  Select = 'select',
  Tel = 'tel',
  Text = 'text',
  TextArea = 'textarea',
}

export enum ValidationRuleType {
  AllOf = 'ValidationRuleAllOf',
  AnyOf = 'ValidationRuleAnyOf',
  Not = 'ValidationRuleNot',
  Pattern = 'ValidationRulePattern',
  Age = 'ValidationRuleAge',
}

interface ValidationRuleBase {
  ruleType: ValidationRuleType;
}

export type ValidationRulePattern = ValidationRuleBase &
  Omit<GqlValidatioRulePattern, 'successMessage'>;

export type ValidationRuleAge = ValidationRuleBase &
  Pick<GqlValidationRuleAge, 'min' | 'max' | 'errorMessage'>;

export interface ValidationRuleRequired extends ValidationRuleBase {
  isRequired: boolean;
}

export interface ValidationRuleGroup extends ValidationRuleBase {
  rules: Array<
    | ValidationRulePattern
    | {
        ruleType: ValidationRuleType.AllOf | ValidationRuleType.AnyOf | ValidationRuleType.Not;
        rules: ValidationRulePattern[];
      }
  >;
}

export type ValidationRule =
  | ValidationRulePattern
  | ValidationRuleRequired
  | ValidationRuleGroup
  | ValidationRuleAge;

export type FormFieldValidationFunction = (
  inputValue: string | Record<string, string>,
  values: Record<string, string>
) => string | boolean;

export interface FormFieldValidationRules {
  required: boolean;
  requiredErrorMessage?: string;
  rules?: Nullable<ValidationRule[]>;
  rulesErrorMessage?: string;
  functions?: {
    [key: string]: FormFieldValidationFunction;
  };
}

interface BaseValidateOptions {
  inputValue: string;
  translate: Translate;
}

export interface ValidatePatternOptions extends BaseValidateOptions {
  rule: ValidationRulePattern;
}

export interface ValidateRulesOption extends BaseValidateOptions {
  rules: ValidationRuleGroup['rules'];
}

export interface ValidateAgeOptions extends Omit<BaseValidateOptions, 'inputValue'> {
  inputValue: BirthDayFieldValue;
  rules: ValidationRuleAge;
}

export type GqlValidationRules = Nullable<Array<ValidationRule | ValidationRuleRequired>>;

export type ValidatorRule = ValidationRulePattern | ValidationRuleGroup | ValidationRuleAge;

export type FormState<TFieldValues extends FieldValues> = UseFormStateReturn<TFieldValues>;
